import type { Filter, Query, ResultSet, TQueryOrderArray } from '@cubejs-client/core'

import {
  VALUE_LIST_LIMIT,
  type ColumnFilter,
  type ColumnFilters,
  type FilterCondition,
  type FilterFieldKind,
} from '@/components/filters/column-filter'
import type { Selection } from '@/lib/cross-filter'
import {
  currencyMembers,
  filterKind,
  type ChartReport,
  type Granularity,
  type ReportDef,
  type ReportField,
  type ReportFilterDef,
  type ReportSortItem,
  type TableReport,
} from '@/lib/cube-report'
import { BLOCK_SIZE } from '@/components/data-table/virtual-blocks'
import { buildCubeQuery, cubeApi } from '@/lib/cube'
import { shiftDateInput } from '@/lib/format'
import { queryKeys } from '@/lib/query-keys'

/** Cube satırı düzdür: anahtarlar nitelenmiş üye adlarıdır (`row['Tartim.tarih']`). */
export type ReportRow = Record<string, unknown>

/** Raporun meta'daki filtrelerinden ve kullanıcı girdisinden oluşan durum (URL'den okunur). */
export interface ReportFilterState {
  dateFrom?: string
  dateTo?: string
  /** multiSelect filtreleri: nitelenmiş üye -> seçili değerler. */
  multi: Record<string, string[]>
  /** boolean filtreleri: nitelenmiş üye -> seçim (yoksa "tümü"). */
  bool: Record<string, boolean | undefined>
  q?: string
  /** Tablo sütun filtreleri (`cf`); yalnız tablo raporu ve pano listesi uygular. */
  columns: ColumnFilters
  /** Çapraz filtre seçimleri (`x`); pano parçaları ve keşfet sayfası uygular. */
  selections: Selection[]
}

const TEXT_OPERATORS = {
  eq: 'equals',
  neq: 'notEquals',
  contains: 'contains',
  notContains: 'notContains',
  startsWith: 'startsWith',
  endsWith: 'endsWith',
} as const

/**
 * Tarih koşulları gün bazında: `datetime` sütunda da tarih girişi var (Excel gibi).
 * Değerler İstanbul takvim günüdür; sorgu `timezone`'u zaten İstanbul (`buildCubeQuery`).
 */
function dateCondition(member: string, condition: FilterCondition): Filter {
  const { value } = condition
  const nextDay = () => shiftDateInput(value, 1)
  switch (condition.op) {
    case 'eq':
      return { member, operator: 'inDateRange', values: [value, value] }
    case 'neq':
      return { member, operator: 'notInDateRange', values: [value, value] }
    case 'gt':
      return { member, operator: 'afterOrOnDate', values: [nextDay()] }
    case 'gte':
      return { member, operator: 'afterOrOnDate', values: [value] }
    case 'lt':
      return { member, operator: 'beforeDate', values: [value] }
    case 'lte':
      return { member, operator: 'beforeDate', values: [nextDay()] }
    default:
      return { member, operator: 'inDateRange', values: [value, condition.valueTo ?? value] }
  }
}

function conditionToCube(
  member: string,
  kind: FilterFieldKind,
  condition: FilterCondition,
): Filter {
  const { op, value } = condition
  if (op === 'set' || op === 'notSet') return { member, operator: op }
  if (kind === 'date' || kind === 'datetime') return dateCondition(member, condition)
  if (op === 'between') {
    return {
      and: [
        { member, operator: 'gte', values: [value] },
        { member, operator: 'lte', values: [condition.valueTo ?? value] },
      ],
    }
  }
  if (op === 'gt' || op === 'gte' || op === 'lt' || op === 'lte') {
    return { member, operator: op, values: [value] }
  }
  return { member, operator: TEXT_OPERATORS[op], values: [value] }
}

/**
 * Sütun filtresi -> tek Cube filtresi (sütunlar arası üst düzeyde VE). Cube'ün
 * `notEquals`'ı boşları (NULL) da döndürür; "boşlar hariç" ayrıca `set` ister.
 */
export function columnFilterToCube(
  member: string,
  kind: FilterFieldKind,
  filter: ColumnFilter,
): Filter {
  if (filter.type === 'conditions') {
    const parts = filter.conditions.map((condition) => conditionToCube(member, kind, condition))
    if (parts.length === 1) return parts[0] as Filter
    return filter.join === 'or' ? { or: parts } : { and: parts }
  }
  const { mode, values, blank } = filter
  if (mode === 'out') {
    const parts: Filter[] = []
    if (values.length > 0) parts.push({ member, operator: 'notEquals', values })
    if (!blank) parts.push({ member, operator: 'set' })
    return parts.length === 1 ? (parts[0] as Filter) : { and: parts }
  }
  const parts: Filter[] = []
  if (values.length > 0) parts.push({ member, operator: 'equals', values })
  if (blank) parts.push({ member, operator: 'notSet' })
  // Hiçbiri seçili değil: Excel'de Tamam kapalıdır; buraya gelirse hiçbir satır dönmesin.
  if (parts.length === 0)
    return {
      and: [
        { member, operator: 'set' },
        { member, operator: 'notSet' },
      ],
    }
  return parts.length === 1 ? (parts[0] as Filter) : { or: parts }
}

/** Sütun filtreleri; `except` (değer listesi sorgulanan sütun) hariç. */
export function buildColumnFilters(
  fields: ReadonlyMap<string, ReportField>,
  columns: ColumnFilters,
  except?: string,
): Filter[] {
  return Object.entries(columns).flatMap(([member, filter]) => {
    const field = fields.get(member)
    if (!field || member === except) return []
    return [columnFilterToCube(member, filterKind(field), filter)]
  })
}

export function dateRangeMember(source: { filters: ReportFilterDef[] }): string | undefined {
  return source.filters.find((filter) => filter.type === 'dateRange')?.member
}

/** Meta filtreleri -> Cube filtreleri. Durum `stateKey` (pano parçası) ya da `member` ile okunur. */
export function buildDefinedFilters(
  defs: ReportFilterDef[],
  state: ReportFilterState,
  { skipDateRange = false }: { skipDateRange?: boolean } = {},
): Filter[] {
  const filters: Filter[] = []

  for (const filter of defs) {
    const key = filter.stateKey ?? filter.member
    if (filter.type === 'dateRange' && !skipDateRange) {
      if (state.dateFrom && !filter.upperBoundOnly) {
        filters.push({ member: filter.member, operator: 'afterOrOnDate', values: [state.dateFrom] })
      }
      if (state.dateTo) {
        filters.push({ member: filter.member, operator: 'beforeOrOnDate', values: [state.dateTo] })
      }
    }
    const selected = state.multi[key]
    if (filter.type === 'multiSelect' && selected && selected.length > 0) {
      filters.push({ member: filter.member, operator: 'equals', values: selected })
    }
    const flag = state.bool[key]
    if (filter.type === 'boolean' && flag !== undefined) {
      filters.push({ member: filter.member, operator: 'equals', values: [flag ? 'true' : 'false'] })
    }
  }
  return filters
}

/**
 * `skipDateRange`: tarih aralığı `timeDimensions` üzerinden verilecekse
 * (dönem karşılaştırma — `compareDateRange` filtreyle birlikte çalışmaz).
 */
export function buildReportFilters(
  report: ReportDef,
  state: ReportFilterState,
  { skipDateRange = false }: { skipDateRange?: boolean } = {},
): Filter[] {
  const filters = buildDefinedFilters(report.filters, state, { skipDateRange })

  if (report.layout === 'table' && state.q && report.search.length > 0) {
    const q = state.q
    filters.push({
      or: report.search.map((member) => ({ member, operator: 'contains' as const, values: [q] })),
    })
  }

  if (report.layout === 'table') filters.push(...buildColumnFilters(report.fields, state.columns))

  return filters
}

/** Varsayılan sıra + her zaman satır kimliğiyle eşitlik bozma (sayfalar arası kararlı sıra). */
export function buildTableOrder(
  report: { defaultSort: ReportSortItem[]; rowKey?: string },
  sort: ReportSortItem[],
): TQueryOrderArray {
  const items = sort.length > 0 ? sort : report.defaultSort
  const order: TQueryOrderArray = items.map((item) => [item.member, item.desc ? 'desc' : 'asc'])
  const { rowKey } = report
  if (rowKey && !items.some((item) => item.member === rowKey)) order.push([rowKey, 'asc'])
  return order
}

/**
 * Sütunun değer listesi sorgusu (Excel gibi): diğer tüm filtreler geçerli, sütunun
 * kendi filtresi hariç. `scope`: o sütun filtresi çıkarılmış durumdan Cube filtreleri.
 */
export function columnValuesQuery(
  view: string,
  member: string,
  search: string,
  scope: (state: ReportFilterState) => Filter[],
  state: ReportFilterState,
) {
  const columns = Object.fromEntries(
    Object.entries(state.columns).filter(([key]) => key !== member),
  )
  const filters = scope({ ...state, columns })
  return {
    queryKey: queryKeys.cube(view, { distinct: member, search, filters }),
    queryFn: () =>
      fetchDistinctValues(member, { q: search || undefined, limit: VALUE_LIST_LIMIT, filters }),
    staleTime: 30_000,
  }
}

export interface ReportPage {
  items: ReportRow[]
  total: number
}

/*
 * Rapor sorguları ikiye ayrılır: saf sorgu kurucu (`*Query`) ve sonuç eşleyici
 * (`to*`). Aynı çift hem ilk yüklemede (HTTP `load`) hem canlı abonelikte (WS
 * `subscribe`) kullanılır (bkz. hooks/use-cube-live-query.ts).
 */

/**
 * Excel'e aktarımda tek sorguda çekilen en fazla satır (Cube'un üst sınırı). Sayfalama
 * ekranda kalır; aktarım aynı filtre ve sıralamayla bütün kayıtları ister.
 */
export const EXPORT_ROW_LIMIT = 50_000

/** Sayfalı ham satır sorgusunun bütün kayıtları (ilk `EXPORT_ROW_LIMIT`), Excel için. */
export async function fetchAllRows(paged: Query): Promise<ReportRow[]> {
  const result = await cubeApi.load({
    ...paged,
    offset: undefined,
    limit: EXPORT_ROW_LIMIT,
    total: false,
  })
  return result.rawData() as ReportRow[]
}

/** `ungrouped: true` + limit/offset + `total: true` — ham satır listesi (bkz. docs/HANDOFF.md G1.0). */
export function pagedQuery(spec: {
  dimensions: string[]
  filters: Filter[]
  order: TQueryOrderArray
  page: number
  pageSize: number
}): Query {
  return buildCubeQuery({
    dimensions: spec.dimensions,
    filters: spec.filters,
    order: spec.order,
    limit: spec.pageSize,
    offset: (spec.page - 1) * spec.pageSize,
    ungrouped: true,
    total: true,
  })
}

export function tablePageQuery(
  report: TableReport,
  state: ReportFilterState,
  sort: ReportSortItem[],
  page: number,
  pageSize: number,
): Query {
  return pagedQuery({
    dimensions: report.dimensions.map((field) => field.name),
    filters: buildReportFilters(report, state),
    order: buildTableOrder(report, sort),
    page,
    pageSize,
  })
}

/**
 * Sanal kaydırmanın `block`. bloğu (0 tabanlı, `blockSize` satır): satır sorgusuyla aynı filtre/sıra,
 * `limit`/`offset` bloğa göre. `total: true` her blokta gelir, bu yüzden toplam en son alınan bloktan okunur.
 */
export function tableBlockQuery(
  report: TableReport,
  state: ReportFilterState,
  sort: ReportSortItem[],
  block: number,
  blockSize: number = BLOCK_SIZE,
): Query {
  return tablePageQuery(report, state, sort, block + 1, blockSize)
}

/**
 * Blok anahtarı `['cube', view, …]` kalıbında kalır (realtime invalidation). Sayfa/blok boyutu dışında yalnız
 * süzgeç ve sıralama girer: `page`/`page_size` URL parametreleri anahtarı etkilemez.
 */
export function tableBlockKey(
  report: Pick<TableReport, 'view'>,
  state: ReportFilterState,
  sort: ReportSortItem[],
  block: number,
  blockSize: number = BLOCK_SIZE,
) {
  return queryKeys.cube(report.view, { filters: state, sort, block, blockSize })
}

/** `meta.table.totals`'taki ölçüler (tekrarsız, meta sırasıyla). */
export function totalsMeasures(report: Pick<TableReport, 'totals'>): string[] {
  return [...new Set(Object.values(report.totals ?? {}))]
}

/**
 * Toplam satırı: tablonun satır sorgusuyla aynı filtreler (sütun filtreleri ve arama dahil),
 * boyutsuz; yalnız ölçüye bağlı para birimi üyesine göre gruplanır (TL ile USD toplanmaz).
 * Sıralama ve sayfa sorguya girmez, onlar değişince yeniden sorgulanmaz.
 */
export function tableTotalsQuery(report: TableReport, state: ReportFilterState): Query {
  const measures = totalsMeasures(report)
  const dimensions = currencyMembers(report.fields, measures)
  return buildCubeQuery({
    measures,
    ...(dimensions.length > 0 && { dimensions }),
    filters: buildReportFilters(report, state),
  })
}

export function toTablePage(result: ResultSet): ReportPage {
  return { items: result.rawData() as ReportRow[], total: result.totalRows() ?? 0 }
}

/** Bir dimension'ın ayrık değerleri — multiSelect seçenekleri ve gelişmiş filtre ara-yazı. */
export async function fetchDistinctValues(
  member: string,
  { q, limit, filters: scope = [] }: { q?: string; limit?: number; filters?: Filter[] } = {},
): Promise<string[]> {
  const filters: Filter[] = q ? [...scope, { member, operator: 'contains', values: [q] }] : scope
  const result = await cubeApi.load(
    buildCubeQuery({ dimensions: [member], filters, order: [[member, 'asc']], limit }),
  )
  const rows = result.rawData() as Record<string, unknown>[]
  return (
    rows
      .map((row) => row[member])
      .filter((value) => value !== null && value !== undefined && value !== '')
      .map(String)
      // Rollup'tan (Cube Store) dönen sıra bayt sırası: "Çek" "Z"nin ardına düşer.
      .sort((a, b) => a.localeCompare(b, 'tr'))
  )
}

/** Grafikteki ve altındaki tablodaki her ölçü tek sorguda. */
export function chartMeasures(report: ChartReport): string[] {
  return [...new Set([...report.series.map((series) => series.measure), ...report.table])]
}

/** Kovanın satırdaki anahtarı: `Tonaj.tarih.day`. */
export function bucketKey(report: ChartReport, granularity: Granularity): string {
  return `${report.time}.${granularity}`
}

/**
 * Tam tarih aralığı `timeDimensions.dateRange` olarak: aynı sonuç, ama filtre olarak
 * verilen aralıkla Cube rollup'a eşleşmiyor (FILTER-PERF-PLAN C4, ölçüldü).
 */
function fullDateRange(
  report: { filters: ReportFilterDef[] },
  state: ReportFilterState,
): { member: string; range: [string, string] } | undefined {
  const member = dateRangeMember(report)
  const { dateFrom, dateTo } = state
  return member && dateFrom && dateTo ? { member, range: [dateFrom, dateTo] } : undefined
}

export function chartSeriesQuery(
  report: ChartReport,
  state: ReportFilterState,
  granularity: Granularity,
): Query {
  const range = fullDateRange(report, state)
  const onTime = range?.member === report.time
  return buildCubeQuery({
    measures: chartMeasures(report),
    timeDimensions: [
      { dimension: report.time, granularity, ...(onTime && { dateRange: range.range }) },
    ],
    filters: buildReportFilters(report, state, { skipDateRange: onTime }),
    order: [[report.time, 'asc']],
  })
}

export function toRows(result: ResultSet): ReportRow[] {
  return result.rawData() as ReportRow[]
}

/** Aynı uzunlukta, hemen önceki takvim aralığı (her iki uç dahil). */
export function previousDateRange(from: string, to: string): [string, string] {
  const days = Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1
  const previousTo = shiftDateInput(from, -1)
  return [shiftDateInput(previousTo, 1 - days), previousTo]
}

export interface ChartSummary {
  current: ReportRow
  previous?: ReportRow
}

export interface SummarySpec {
  measures: string[]
  /** Gruplama (ör. döviz üyesi); yoksa tek satır. */
  dimensions?: string[]
  /** Sayfa tarih aralığı filtresinin üyesi. */
  rangeMember?: string
  /** `skipDateRange`: aralık `timeDimensions`'tan verildiğinde tarih filtresi çıkarılır. */
  filters: (skipDateRange: boolean) => Filter[]
}

/**
 * Özet sorgusu (rapor özeti ve keşfet KPI'ları ortak). Tam bir tarih aralığı varsa
 * `timeDimensions`'tan verilir; `compare` ile Cube'ün `compareDateRange`'i önceki
 * dönemi aynı sorguda getirir.
 */
export function summaryQuery(spec: SummarySpec, state: ReportFilterState, compare: boolean): Query {
  const { measures, dimensions, rangeMember } = spec
  const { dateFrom, dateTo } = state
  const grouping = dimensions && dimensions.length > 0 ? { dimensions } : {}

  if (!rangeMember || !dateFrom || !dateTo) {
    return buildCubeQuery({ measures, ...grouping, filters: spec.filters(false) })
  }
  const timeDimension = compare
    ? {
        dimension: rangeMember,
        compareDateRange: [[dateFrom, dateTo], previousDateRange(dateFrom, dateTo)] as [
          [string, string],
          [string, string],
        ],
      }
    : { dimension: rangeMember, dateRange: [dateFrom, dateTo] as [string, string] }
  return buildCubeQuery({
    measures,
    ...grouping,
    timeDimensions: [timeDimension],
    filters: spec.filters(true),
  })
}

export function chartSummaryQuery(
  report: ChartReport,
  state: ReportFilterState,
  compare: boolean,
): Query {
  return summaryQuery(
    {
      measures: report.summary,
      rangeMember: dateRangeMember(report),
      filters: (skipDateRange) => buildReportFilters(report, state, { skipDateRange }),
    },
    state,
    compare,
  )
}

/** Karşılaştırmasız sorguda `decompose()` tek parça döner, `previous` boş kalır. */
export function toChartSummary(result: ResultSet): ChartSummary {
  const [current, previous] = result.decompose().map((set) => (set.rawData()[0] ?? {}) as ReportRow)
  return { current: current ?? {}, previous }
}
